import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import DatePickerField from '../../components/common/DatePickerField';
import riskAssessmentService from '../../services/riskAssessmentService';
import projectService from '../../services/projectService';
import teamService from '../../services/teamService';
import { useAuth } from '../../contexts/AuthContext';
import { formatCategory, getTrackedCategoryOptions } from '../inspections/categoryOptions';
import './RiskAssessments.css';

// 1=Rare ... 5=Almost Certain
const LIKELIHOOD_OPTIONS = [
  { value: 1, label: 'Rare', description: 'May occur only in exceptional circumstances' },
  { value: 2, label: 'Unlikely', description: 'Could occur at some point' },
  { value: 3, label: 'Possible', description: 'Might occur at some point' },
  { value: 4, label: 'Likely', description: 'Will probably occur in most circumstances' },
  { value: 5, label: 'Almost Certain', description: 'Expected to occur in most circumstances' },
];

// 1=Negligible ... 5=Catastrophic
const SEVERITY_OPTIONS = [
  { value: 1, label: 'Negligible', description: 'No injury or negligible impact' },
  { value: 2, label: 'Minor', description: 'First aid treatment, minor impact' },
  { value: 3, label: 'Moderate', description: 'Medical treatment, moderate impact' },
  { value: 4, label: 'Major', description: 'Serious injury or major impact' },
  { value: 5, label: 'Catastrophic', description: 'Death or catastrophic/irreversible impact' },
];

const STATUS_OPTIONS = ['open', 'controlled', 'closed'];

const BAND_COLOR = {
  Low: '#16a34a',
  Moderate: '#f59e0b',
  High: '#ef4444',
  Extreme: '#7f1d1d',
};

/**
 * Client-side mirror of the backend's RiskAssessmentController.getBand, used
 * only for instant preview while filling the form / rendering the matrix.
 * The server is always the source of truth for the persisted riskBand.
 */
const getBand = (score) => {
  if (score >= 15) return 'Extreme';
  if (score >= 8) return 'High';
  if (score >= 4) return 'Moderate';
  return 'Low';
};

const likelihoodLabel = (value) => LIKELIHOOD_OPTIONS.find((o) => o.value === value)?.label || value;
const severityLabel = (value) => SEVERITY_OPTIONS.find((o) => o.value === value)?.label || value;

const formatStatus = (status) =>
  String(status || '').split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

const RiskAssessments = () => {
  const [loading, setLoading] = useState(true);
  const [riskAssessments, setRiskAssessments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterBand, setFilterBand] = useState('all');
  const [matrixFilter, setMatrixFilter] = useState(null); // { likelihood, severity } | null

  const fetchRiskAssessments = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterBand !== 'all') params.riskBand = filterBand;
      const data = await riskAssessmentService.getAll(params);
      setRiskAssessments(data.riskAssessments || []);
    } catch (error) {
      console.error('Failed to fetch risk assessments:', error);
      toast.error(error.response?.data?.error || 'Failed to load risk assessments');
    } finally {
      setLoading(false);
    }
  }, [filterBand]);

  useEffect(() => {
    fetchRiskAssessments();
  }, [fetchRiskAssessments]);

  useEffect(() => {
    (async () => {
      try {
        const data = await projectService.getAll();
        setProjects(data.projects || []);
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      }
    })();
    // Team list is admin-only server-side; non-admins simply get an empty
    // list and the "Assigned To" field is left as unassigned-only.
    (async () => {
      try {
        const data = await teamService.list();
        setTeamMembers(data.users || data.teamMembers || []);
      } catch (error) {
        setTeamMembers([]);
      }
    })();
  }, []);

  const stats = {
    total: riskAssessments.length,
    extreme: riskAssessments.filter((r) => r.riskBand === 'Extreme').length,
    high: riskAssessments.filter((r) => r.riskBand === 'High').length,
    open: riskAssessments.filter((r) => r.status === 'open').length,
  };

  const visibleAssessments = matrixFilter
    ? riskAssessments.filter((r) => r.likelihood === matrixFilter.likelihood && r.severity === matrixFilter.severity)
    : riskAssessments;

  const handleCreate = async (data) => {
    try {
      setSaving(true);
      await riskAssessmentService.create(data);
      toast.success('Risk assessment created successfully');
      setShowModal(false);
      fetchRiskAssessments();
    } catch (error) {
      console.error('Failed to create risk assessment:', error);
      toast.error(error.response?.data?.error || 'Failed to create risk assessment');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (data) => {
    if (!editingItem) return;
    try {
      setSaving(true);
      await riskAssessmentService.update(editingItem.id, data);
      toast.success('Risk assessment updated successfully');
      setEditingItem(null);
      fetchRiskAssessments();
    } catch (error) {
      console.error('Failed to update risk assessment:', error);
      toast.error(error.response?.data?.error || 'Failed to update risk assessment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete this risk assessment for "${item.activity}"? This cannot be undone.`)) return;
    try {
      await riskAssessmentService.delete(item.id);
      toast.success('Risk assessment deleted');
      fetchRiskAssessments();
    } catch (error) {
      console.error('Failed to delete risk assessment:', error);
      toast.error(error.response?.data?.error || 'Failed to delete risk assessment');
    }
  };

  const handleStatusChange = async (item, status) => {
    try {
      await riskAssessmentService.update(item.id, { status });
      toast.success('Status updated');
      fetchRiskAssessments();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error(error.response?.data?.error || 'Failed to update status');
    }
  };

  return (
    <div className="risk-assessment-page">
      <div className="ra-header">
        <div className="header-content">
          <div className="header-title">
            <h1><i className="fas fa-exclamation-circle"></i> Risk Assessment Matrix</h1>
            <p className="header-subtitle">Score hazards by likelihood x severity and prioritise controls</p>
          </div>
          <button className="btn-primary-green" onClick={() => setShowModal(true)}>
            <i className="fas fa-plus"></i> New Risk Assessment
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon"><i className="fas fa-clipboard-list"></i></div>
          <div className="stat-details">
            <div className="stat-label">Total Assessments</div>
            <div className="stat-value">{stats.total}</div>
          </div>
        </div>
        <div className="stat-card extreme">
          <div className="stat-icon"><i className="fas fa-skull-crossbones"></i></div>
          <div className="stat-details">
            <div className="stat-label">Extreme Risk</div>
            <div className="stat-value">{stats.extreme}</div>
          </div>
        </div>
        <div className="stat-card danger">
          <div className="stat-icon"><i className="fas fa-exclamation-triangle"></i></div>
          <div className="stat-details">
            <div className="stat-label">High Risk</div>
            <div className="stat-value">{stats.high}</div>
          </div>
        </div>
        <div className="stat-card warning">
          <div className="stat-icon"><i className="fas fa-lock-open"></i></div>
          <div className="stat-details">
            <div className="stat-label">Open</div>
            <div className="stat-value">{stats.open}</div>
          </div>
        </div>
      </div>

      <RiskMatrix
        riskAssessments={riskAssessments}
        activeCell={matrixFilter}
        onCellClick={(cell) => setMatrixFilter((prev) =>
          prev && prev.likelihood === cell.likelihood && prev.severity === cell.severity ? null : cell
        )}
      />

      <div className="ra-controls">
        <div className="filter-group">
          {['all', 'Low', 'Moderate', 'High', 'Extreme'].map((band) => (
            <button
              key={band}
              className={`filter-btn ${filterBand === band ? 'active' : ''}`}
              onClick={() => setFilterBand(band)}
            >
              {band === 'all' ? 'All Bands' : band}
            </button>
          ))}
        </div>
        {matrixFilter && (
          <button className="btn-text" onClick={() => setMatrixFilter(null)}>
            <i className="fas fa-times"></i> Clear matrix filter (L{matrixFilter.likelihood} x S{matrixFilter.severity})
          </button>
        )}
      </div>

      {loading ? (
        <div className="empty-state">
          <i className="fas fa-spinner fa-spin"></i>
          <h3>Loading risk assessments...</h3>
        </div>
      ) : visibleAssessments.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-check-circle"></i>
          <h3>No risk assessments found</h3>
          <p>Raise a risk assessment to score a hazard and plot it on the matrix</p>
        </div>
      ) : (
        <div className="ra-register">
          <table className="ra-table">
            <thead>
              <tr>
                <th>Activity / Hazard</th>
                <th>Category</th>
                <th>Likelihood</th>
                <th>Severity</th>
                <th>Score</th>
                <th>Band</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visibleAssessments.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className="ra-activity">{item.activity}</div>
                    <div className="ra-hazard">{item.hazardDescription}</div>
                  </td>
                  <td>{formatCategory(item.category)}</td>
                  <td>{likelihoodLabel(item.likelihood)} ({item.likelihood})</td>
                  <td>{severityLabel(item.severity)} ({item.severity})</td>
                  <td className="ra-score">{item.riskScore}</td>
                  <td>
                    <span className="ra-band-badge" style={{ backgroundColor: BAND_COLOR[item.riskBand] }}>
                      {item.riskBand}
                    </span>
                  </td>
                  <td>
                    <select
                      className="filter-select"
                      value={item.status}
                      onChange={(e) => handleStatusChange(item, e.target.value)}
                    >
                      {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>{formatStatus(status)}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <div className="ra-actions">
                      <button className="btn-icon" title="Edit" onClick={() => setEditingItem(item)}>
                        <i className="fas fa-edit"></i>
                      </button>
                      <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(item)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <RiskAssessmentModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSave={handleCreate}
          projects={projects}
          teamMembers={teamMembers}
          saving={saving}
        />
      )}

      {editingItem && (
        <RiskAssessmentModal
          isOpen={!!editingItem}
          onClose={() => setEditingItem(null)}
          onSave={handleUpdate}
          projects={projects}
          teamMembers={teamMembers}
          saving={saving}
          item={editingItem}
        />
      )}
    </div>
  );
};

/**
 * The visual 5x5 risk assessment matrix - the key differentiator of this
 * page vs. the generic category overview. Severity runs top-to-bottom
 * (5=Catastrophic at top), likelihood runs left-to-right (1=Rare to
 * 5=Almost Certain), mirroring the standard HSE RAM layout. Each cell shows
 * its score and how many current assessments plot there; clicking a cell
 * filters the register below.
 */
const RiskMatrix = ({ riskAssessments, activeCell, onCellClick }) => {
  const countFor = (likelihood, severity) =>
    riskAssessments.filter((r) => r.likelihood === likelihood && r.severity === severity).length;

  return (
    <div className="risk-matrix-card">
      <h3 className="risk-matrix-title">5 x 5 Risk Matrix</h3>
      <div className="risk-matrix-wrapper">
        <div className="risk-matrix-axis-label risk-matrix-y-label">Severity</div>
        <div className="risk-matrix-grid-area">
          <table className="risk-matrix-table">
            <tbody>
              {[5, 4, 3, 2, 1].map((severity) => (
                <tr key={severity}>
                  <td className="risk-matrix-row-label">
                    {severityLabel(severity)}<span className="risk-matrix-row-num">{severity}</span>
                  </td>
                  {[1, 2, 3, 4, 5].map((likelihood) => {
                    const score = likelihood * severity;
                    const band = getBand(score);
                    const count = countFor(likelihood, severity);
                    const isActive = activeCell && activeCell.likelihood === likelihood && activeCell.severity === severity;
                    return (
                      <td
                        key={likelihood}
                        className={`risk-matrix-cell ${isActive ? 'active' : ''}`}
                        style={{ backgroundColor: BAND_COLOR[band] }}
                        onClick={() => onCellClick({ likelihood, severity })}
                        title={`${severityLabel(severity)} x ${likelihoodLabel(likelihood)} = ${score} (${band})`}
                      >
                        <span className="risk-matrix-score">{score}</span>
                        {count > 0 && <span className="risk-matrix-count">{count}</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr>
                <td></td>
                {[1, 2, 3, 4, 5].map((likelihood) => (
                  <td key={likelihood} className="risk-matrix-col-label">
                    {likelihoodLabel(likelihood)}<span className="risk-matrix-row-num">{likelihood}</span>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="risk-matrix-x-label">Likelihood</div>
      <div className="risk-matrix-legend">
        {Object.entries(BAND_COLOR).map(([band, color]) => (
          <div key={band} className="risk-matrix-legend-item">
            <span className="risk-matrix-legend-swatch" style={{ backgroundColor: color }}></span>
            {band}
          </div>
        ))}
      </div>
    </div>
  );
};

const RiskAssessmentModal = ({ isOpen, onClose, onSave, projects, teamMembers, saving, item }) => {
  const isEditing = !!item;
  const { organisation } = useAuth();

  // Only offer categories this org actually tracks (plus its custom ones) -
  // an existing assessment's category is always kept selectable even if the
  // org later stopped tracking it.
  const categoryOptions = useMemo(() => (
    getTrackedCategoryOptions(organisation?.trackingCategories, organisation?.customCategories || [], item?.category || null)
  ), [organisation, item]);

  const [formData, setFormData] = useState(() => ({
    projectId: item?.projectId || '',
    category: item?.category || categoryOptions[0]?.value || 'osh',
    activity: item?.activity || '',
    hazardDescription: item?.hazardDescription || '',
    existingControls: item?.existingControls || '',
    likelihood: item?.likelihood || 3,
    severity: item?.severity || 3,
    additionalControls: item?.additionalControls || '',
    assignedTo: item?.assignedTo || '',
    reviewDate: item?.reviewDate || '',
    status: item?.status || 'open',
  }));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      likelihood: parseInt(formData.likelihood, 10),
      severity: parseInt(formData.severity, 10),
      projectId: formData.projectId || null,
      assignedTo: formData.assignedTo || null,
      reviewDate: formData.reviewDate || null,
    });
  };

  const previewScore = parseInt(formData.likelihood, 10) * parseInt(formData.severity, 10);
  const previewBand = getBand(previewScore);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="ra-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2><i className="fas fa-exclamation-circle"></i> {isEditing ? 'Edit Risk Assessment' : 'New Risk Assessment'}</h2>
          <button className="close-btn" onClick={onClose}><i className="fas fa-times"></i></button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-content">
            <div className="form-section">
              <h3 className="section-title">Hazard Details</h3>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Activity / Task *</label>
                  <input type="text" name="activity" value={formData.activity} onChange={handleInputChange} placeholder="e.g., Delivering products to site" required />
                </div>

                <div className="form-group">
                  <label>Category</label>
                  <select name="category" value={formData.category} onChange={handleInputChange}>
                    {categoryOptions.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Project (optional)</label>
                  <Select
                    name="projectId"
                    value={projects.find((p) => p.id === formData.projectId)
                      ? { label: projects.find((p) => p.id === formData.projectId).name, value: formData.projectId }
                      : null}
                    onChange={(selected) => setFormData((prev) => ({ ...prev, projectId: selected?.value || '' }))}
                    options={projects.map((p) => ({ label: `${p.name} (${p.code || 'No code'})`, value: p.id }))}
                    placeholder="Org-wide (no specific project)..."
                    isClearable
                    isSearchable
                    classNamePrefix="select"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Hazard Description *</label>
                  <textarea name="hazardDescription" value={formData.hazardDescription} onChange={handleInputChange} placeholder="Describe the hazard, e.g. Vehicle collision with pedestrian" rows="3" required />
                </div>

                <div className="form-group full-width">
                  <label>Existing Controls</label>
                  <textarea name="existingControls" value={formData.existingControls} onChange={handleInputChange} placeholder="Controls already in place" rows="2" />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="section-title"><i className="fas fa-th"></i> Risk Scoring</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Likelihood *</label>
                  <select name="likelihood" value={formData.likelihood} onChange={handleInputChange} required>
                    {LIKELIHOOD_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.value} - {o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Severity *</label>
                  <select name="severity" value={formData.severity} onChange={handleInputChange} required>
                    {SEVERITY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.value} - {o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group full-width">
                  <div className="ra-score-preview">
                    <span>Risk Score: <strong>{previewScore}</strong></span>
                    <span className="ra-band-badge" style={{ backgroundColor: BAND_COLOR[previewBand] }}>{previewBand}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="section-title"><i className="fas fa-tasks"></i> Controls & Follow-up</h3>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Additional Controls Recommended</label>
                  <textarea name="additionalControls" value={formData.additionalControls} onChange={handleInputChange} placeholder="Further controls to bring the risk as low as reasonably practicable" rows="2" />
                </div>
                <div className="form-group">
                  <label>Assigned To</label>
                  <select name="assignedTo" value={formData.assignedTo} onChange={handleInputChange}>
                    <option value="">Unassigned</option>
                    {teamMembers.map((member) => (
                      <option key={member.id} value={member.id}>{member.firstName} {member.lastName}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Review Date</label>
                  <DatePickerField name="reviewDate" value={formData.reviewDate} onChange={handleInputChange} placeholder="Select review date" />
                </div>
                {isEditing && (
                  <div className="form-group">
                    <label>Status</label>
                    <select name="status" value={formData.status} onChange={handleInputChange}>
                      {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>{formatStatus(status)}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary-green" disabled={saving}>
              <i className="fas fa-check"></i> {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Risk Assessment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RiskAssessments;
